import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { BillingService } from "../../services/BillingService";
import { usePermissions } from "../../PermissionsProvider";
import { Alert, AlertDescription } from "../ui/alert";
import { CheckCircleIcon, AlertCircleIcon } from "lucide-react";
import { PaymentMethod } from "../../model_gen/billing";
import { Skeleton } from "../ui/skeleton";

interface AddCreditsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddCreditsModal: React.FC<AddCreditsModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { apiKey } = usePermissions();
  const [amount, setAmount] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] =
    useState<boolean>(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    number | null
  >(null);

  const presetAmounts = [10, 25, 50, 100, 250, 500];

  useEffect(() => {
    if (open && apiKey) {
      fetchPaymentMethods();
    }
  }, [open, apiKey]);

  const fetchPaymentMethods = () => {
    setLoadingPaymentMethods(true);

    BillingService.getPaymentMethods(null, apiKey).subscribe(
      (result: PaymentMethod[]) => {
        setPaymentMethods(result);
        setLoadingPaymentMethods(false);

        // Default to the default payment method
        const defaultMethod = result.find((pm) => pm.is_default);
        if (defaultMethod) {
          setSelectedPaymentMethodId(defaultMethod.id || null);
        } else if (result.length > 0) {
          setSelectedPaymentMethodId(result[0].id || null);
        }
      },
      (error: any) => {
        console.error("Error fetching payment methods:", error);
        setLoadingPaymentMethods(false);
      }
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);

    if (isNaN(numAmount) || numAmount <= 0) {
      setError("Please enter a valid amount greater than $0");
      return;
    }

    if (numAmount > 10000) {
      setError(
        "Maximum amount is $10,000. Please contact support for larger amounts."
      );
      return;
    }

    if (!selectedPaymentMethodId) {
      setError("Please select a payment method");
      return;
    }

    setLoading(true);
    setError(null);

    BillingService.addCredits(
      numAmount,
      "usd",
      selectedPaymentMethodId,
      null,
      apiKey
    ).subscribe(
      () => {
        setSuccess(true);
        setLoading(false);
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 1500);
      },
      (error: any) => {
        console.error("Error adding credits:", error);
        setError(error.message || "Failed to add credits. Please try again.");
        setLoading(false);
      }
    );
  };

  const handleClose = () => {
    setAmount("");
    setError(null);
    setSuccess(false);
    onClose();
  };

  const handlePresetClick = (presetAmount: number) => {
    setAmount(presetAmount.toString());
    setError(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Credits</DialogTitle>
          <DialogDescription>
            Add credits to your account. Select a payment method to charge.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8">
            <Alert className="border-maitai-green bg-maitai-green/10">
              <CheckCircleIcon className="h-4 w-4 text-maitai-green" />
              <AlertDescription className="text-maitai-green">
                Credits added successfully! Your balance will update shortly.
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-6 py-4">
              {/* Preset Amounts */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Quick Select</Label>
                <div className="grid grid-cols-3 gap-2">
                  {presetAmounts.map((preset) => (
                    <Button
                      key={preset}
                      type="button"
                      variant={
                        amount === preset.toString() ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => handlePresetClick(preset)}
                      className="w-full">
                      ${preset}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div className="space-y-2">
                <Label htmlFor="amount" className="text-sm font-medium">
                  Custom Amount
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    $
                  </span>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0"
                    max="10000"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => {
                      setAmount(e.target.value);
                      setError(null);
                    }}
                    className="pl-7"
                    disabled={loading}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Minimum: $1.00 | Maximum: $10,000
                </p>
              </div>

              {/* Payment Method Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Payment Method</Label>
                {loadingPaymentMethods ? (
                  <Skeleton className="h-10 w-full" />
                ) : paymentMethods.length > 0 ? (
                  <select
                    value={selectedPaymentMethodId || ""}
                    onChange={(e) =>
                      setSelectedPaymentMethodId(parseInt(e.target.value))
                    }
                    disabled={loading}
                    className="w-full h-10 px-3 text-sm rounded-md border border-border bg-background">
                    {paymentMethods.map((pm) => (
                      <option key={pm.id} value={pm.id || ""}>
                        {pm.meta?.card?.brand} •••• {pm.meta?.card?.last4}
                        {pm.is_default ? " (Default)" : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Alert>
                    <AlertCircleIcon className="h-4 w-4" />
                    <AlertDescription>
                      No payment methods found. Please add a payment method
                      first.
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircleIcon className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  loading ||
                  !amount ||
                  !selectedPaymentMethodId ||
                  paymentMethods.length === 0
                }>
                {loading ? "Adding..." : "Add Credits"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddCreditsModal;
